import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { 
  AlertTriangle, 
  DollarSign, 
  Clock, 
  Scale,
  FileText,
  TrendingUp,
  RefreshCw,
  Filter,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Category color mapping
const CATEGORY_COLORS = {
  'THIRD_PARTY_LIABILITY': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  'MEDICAL_NECESSITY': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  'AUTHORIZATION': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  'TIMELY_FILING': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  'CODING_ERROR': { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  'DUPLICATE_CLAIM': { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' },
  'PATIENT_ELIGIBILITY': { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  'OTHER': { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' }
};

const APPEALABILITY_COLORS = {
  'APPEALABLE': { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  'CONDITIONAL': { bg: 'bg-amber-100', text: 'text-amber-700' },
  'NOT_APPEALABLE': { bg: 'bg-red-100', text: 'text-red-700' },
  'REVIEW_REQUIRED': { bg: 'bg-blue-100', text: 'text-blue-700' }
};

function StatCard({ title, value, subtitle, icon: Icon, trend, color = 'primary' }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardDescription className="text-xs">{title}</CardDescription>
          <Icon className={`w-4 h-4 text-${color}`} />
        </div>
        <CardTitle className="text-2xl">{value}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{subtitle}</span>
          {trend && (
            <span className={`text-xs flex items-center ${trend > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              <TrendingUp className={`w-3 h-3 mr-1 ${trend < 0 ? 'rotate-180' : ''}`} />
              {Math.abs(trend)}%
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CategoryBreakdown({ data }) {
  const categories = Object.entries(data || {}).sort((a, b) => b[1].amount - a[1].amount);
  const totalAmount = categories.reduce((sum, [_, v]) => sum + v.amount, 0);

  if (categories.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
        <p>No denial data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {categories.map(([category, stats]) => {
        const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS.OTHER;
        const percentage = totalAmount > 0 ? (stats.amount / totalAmount) * 100 : 0;
        
        return (
          <div key={category} className={`p-3 rounded-lg border ${colors.bg} ${colors.border}`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium ${colors.text}`}>
                {category.replace(/_/g, ' ')}
              </span>
              <Badge variant="secondary" className="text-xs">
                {stats.count} denial{stats.count !== 1 ? 's' : ''}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <Progress value={percentage} className="h-2" />
              </div>
              <span className="text-sm font-bold">${stats.amount.toLocaleString()}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AppealabilityChart({ data }) {
  const items = Object.entries(data || {});
  const total = items.reduce((sum, [_, v]) => sum + v.count, 0);

  return (
    <div className="space-y-4">
      {items.map(([status, stats]) => {
        const colors = APPEALABILITY_COLORS[status] || APPEALABILITY_COLORS.REVIEW_REQUIRED;
        const percentage = total > 0 ? (stats.count / total) * 100 : 0;

        return (
          <div key={status} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className={`px-2 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                {status.replace(/_/g, ' ')}
              </span>
              <span className="font-medium">{stats.count} (${stats.amount.toLocaleString()})</span>
            </div>
            <Progress value={percentage} className="h-1.5" />
          </div>
        );
      })}
    </div>
  );
}

function DenialsTable({ denials, isLoading }) {
  const navigate = useNavigate();

  const getDeadlineBadge = (days) => {
    if (days === null || days === undefined) return null;
    if (days <= 0) return <Badge variant="destructive">Expired</Badge>;
    if (days <= 14) return <Badge className="bg-red-100 text-red-700">Urgent: {days}d</Badge>;
    if (days <= 30) return <Badge className="bg-amber-100 text-amber-700">{days} days</Badge>;
    return <Badge variant="outline">{days} days</Badge>;
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16" />)}
      </div>
    );
  }

  if (!denials || denials.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p>No denials found</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead>Reason Code</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Denied Amount</TableHead>
            <TableHead>Appealability</TableHead>
            <TableHead>Success Rate</TableHead>
            <TableHead>Deadline</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {denials.map((denial) => {
            const categoryColors = CATEGORY_COLORS[denial.category] || CATEGORY_COLORS.OTHER;
            const appealColors = APPEALABILITY_COLORS[denial.appealability] || APPEALABILITY_COLORS.REVIEW_REQUIRED;
            
            return (
              <TableRow key={denial.id} data-testid="denial-row">
                <TableCell>
                  <div>
                    <span className="font-mono font-medium">{denial.ansi_reason_code || 'N/A'}</span>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-[200px] truncate">
                      {denial.code_description || denial.denial_reason}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={`${categoryColors.bg} ${categoryColors.text} ${categoryColors.border} border`}>
                    {(denial.category || 'OTHER').replace(/_/g, ' ')}
                  </Badge>
                </TableCell>
                <TableCell className="font-bold">
                  ${(denial.denied_amount || 0).toLocaleString()}
                </TableCell>
                <TableCell>
                  <Badge className={`${appealColors.bg} ${appealColors.text}`}>
                    {(denial.appealability || 'REVIEW_REQUIRED').replace(/_/g, ' ')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Progress 
                      value={(denial.appeal_success_likelihood || 0) * 100} 
                      className="w-12 h-1.5" 
                    />
                    <span className="text-xs font-mono">
                      {Math.round((denial.appeal_success_likelihood || 0) * 100)}%
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {getDeadlineBadge(denial.days_to_deadline)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {denial.appealability === 'APPEALABLE' && (
                      <Button size="sm" variant="outline" onClick={() => navigate('/appeals')}>
                        <Scale className="w-3 h-3 mr-1" />
                        Appeal
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => navigate(`/documents/${denial.document_id}`)}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function DenialsPage() {
  const [categoryFilter, setCategoryFilter] = useState('');
  const [appealableOnly, setAppealableOnly] = useState(false);
  const [urgentOnly, setUrgentOnly] = useState(false);

  // Fetch denial statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['denialStats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/detect/stats`);
      if (!res.ok) throw new Error('Failed to fetch denial stats');
      return res.json();
    },
    refetchInterval: 30000
  });

  // Fetch denial records
  const { data: denialsData, isLoading: denialsLoading, refetch } = useQuery({
    queryKey: ['denials', categoryFilter, appealableOnly, urgentOnly],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category', categoryFilter);
      if (appealableOnly) params.append('appealable_only', 'true');
      if (urgentOnly) params.append('urgent_only', 'true');
      params.append('limit', '50');
      
      const res = await fetch(`${API_URL}/api/detect/denials?${params}`);
      if (!res.ok) throw new Error('Failed to fetch denials');
      return res.json();
    }
  });

  // Calculate appeal opportunity
  const appealableAmount = stats?.by_appealability?.APPEALABLE?.amount || 0;
  const conditionalAmount = stats?.by_appealability?.CONDITIONAL?.amount || 0;
  const totalOpportunity = appealableAmount + conditionalAmount;

  if (statsLoading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="denials-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-heading">Denial Detection</h1>
          <p className="text-muted-foreground mt-1">Analyze and manage claim denials</p>
        </div>
        <div className="flex items-center gap-3 mt-4 sm:mt-0">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Denials"
          value={stats?.total_denials || 0}
          subtitle="All time"
          icon={XCircle}
          color="red-500"
        />
        <StatCard
          title="Denied Amount"
          value={`$${(stats?.total_denied_amount || 0).toLocaleString()}`}
          subtitle="Total value"
          icon={DollarSign}
          color="red-500"
        />
        <StatCard
          title="Appeal Opportunity"
          value={`$${totalOpportunity.toLocaleString()}`}
          subtitle="Recoverable"
          icon={Scale}
          color="emerald-500"
        />
        <StatCard
          title="Urgent Actions"
          value={stats?.urgent_denials || 0}
          subtitle="Due within 14 days"
          icon={Clock}
          color="amber-500"
        />
      </div>

      {/* Analysis Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-primary" />
              Denials by Category
            </CardTitle>
            <CardDescription>Root cause analysis of all denials</CardDescription>
          </CardHeader>
          <CardContent>
            <CategoryBreakdown data={stats?.by_category} />
          </CardContent>
        </Card>

        {/* Appealability Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Scale className="w-5 h-5 text-primary" />
              Appealability Analysis
            </CardTitle>
            <CardDescription>Potential recovery opportunities</CardDescription>
          </CardHeader>
          <CardContent>
            <AppealabilityChart data={stats?.by_appealability} />
            
            {totalOpportunity > 0 && (
              <div className="mt-6 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-emerald-800">Recovery Potential</p>
                    <p className="text-xs text-emerald-600 mt-0.5">
                      {stats?.by_appealability?.APPEALABLE?.count || 0} appealable + {stats?.by_appealability?.CONDITIONAL?.count || 0} conditional denials
                    </p>
                  </div>
                  <span className="text-2xl font-bold text-emerald-700">
                    ${totalOpportunity.toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Denials Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Denial Records
              </CardTitle>
              <CardDescription>
                {denialsData?.total || 0} total records
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=" ">All Categories</SelectItem>
                  <SelectItem value="THIRD_PARTY_LIABILITY">Third Party Liability</SelectItem>
                  <SelectItem value="MEDICAL_NECESSITY">Medical Necessity</SelectItem>
                  <SelectItem value="AUTHORIZATION">Authorization</SelectItem>
                  <SelectItem value="TIMELY_FILING">Timely Filing</SelectItem>
                  <SelectItem value="CODING_ERROR">Coding Error</SelectItem>
                  <SelectItem value="DUPLICATE_CLAIM">Duplicate Claim</SelectItem>
                  <SelectItem value="PATIENT_ELIGIBILITY">Patient Eligibility</SelectItem>
                </SelectContent>
              </Select>
              
              <Button 
                variant={appealableOnly ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setAppealableOnly(!appealableOnly)}
              >
                Appealable Only
              </Button>
              
              <Button 
                variant={urgentOnly ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setUrgentOnly(!urgentOnly)}
              >
                Urgent Only
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DenialsTable denials={denialsData?.denials} isLoading={denialsLoading} />
        </CardContent>
      </Card>
    </div>
  );
}
